<template>
    <main class="p-2 w-full h-full flex flex-col items-start min-h-main">
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            {{ $t('commons.bundle') }}
                        </th>
                        <th class="text-left border-r p-1">
                            {{ $t('commons.object') }}
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div :title="$t('commons.version')">
                                {{ $t('commons.versionShort') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            {{ $t('invoices.emissionDateShort') }}
                        </th>
                        <th class="text-center border-r p-1" colspan="2" style="width: 9rem">
                            <div class="flex flex-col">
                                <span>{{ $t('commons.amountTTC') }}</span>
                                ({{ $n(filteredTotal, currencyFormat) }})
                            </div>
                        </th>
                        <th style="width: 6rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div>
                                {{ $t('commons.status') }}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="invoice in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="text-left border-r p-1">
                                <div
                                    :title="
                                        invoice.emitter && invoice.emitter.company ? invoice.emitter.company.name : ''
                                    "
                                >
                                    {{ invoice.emitter ? invoice.emitter.label : '' }}
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <a :href="`https://tex.moe/project/${$route.params.projectId}/invoices/${invoice.id}`">
                                    <span class="font-bold">{{ invoice.object }}</span>
                                    <span v-if="invoice.code" class="mx-1">#{{ invoice.code }}</span>
                                    <span v-if="!invoice.object || invoice.object.trim().length === 0">...</span>
                                </a>
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                {{ invoice.index }}
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                {{ invoice.emissionDate | humanizeDate }}
                            </td>
                            <td class="border-r p-1">
                                <div class="text-right w-full">
                                    {{ invoice.amountTTC ? $n(invoice.amountTTC, currencyFormat) : '-' }}
                                </div>
                            </td>
                            <td class="border-r p-1" style="width: 20px">
                                <span
                                    :title="
                                        invoice.distribution?.length > 1 ? $t('invoices.distributedAmountTips') : ''
                                    "
                                >
                                    <icon-arrow-decision-outline
                                        width="16px"
                                        heigth="16px"
                                        :class="{ 'opacity-0': invoice.distribution?.length <= 1 }"
                                    />
                                </span>
                            </td>
                            <td class="text-left border-r p-1" :class="invoice.invoiceStatusColor">
                                {{ $t('invoices.status.' + invoice.invoiceStatus) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { isMobile } from '@/state/state';
import { filterMatch, getMapById, sortBy, uniqBy } from '@/services/sanitize.service';
import { getInvoices } from '@/features/invoices/invoices.service';
import { queryProject } from '@/features/projects/projects.service';

import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { humanizeDate } from '@/filters/dateFilter';
import { getInvoicesExport } from '@/features/invoices/invoicesExports.service';

export default {
    components: {},
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.readOnly = !project.me.allowedFeatures.includes('project_invoices');
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotal() {
            return this.filteredItems.reduce((acc, invoice) => (invoice.amountTTC ? acc + invoice.amountTTC : acc), 0);
        },
        filteredItems() {
            let result = uniqBy(this.filterFn(this.filterValue), 'groupId');
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    const text = item.code ? item.code + ' ' + item.object : item.object;
                    if (this.sortKey === 'object') {
                        return text;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate + ' ' + text;
                    } else if (this.sortKey === 'emitter') {
                        return (item.bundle ? item.bundle.label : null) + text;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        humanizeDate,
        async init() {
            await this.restoreFilter();
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                ]).subscribe(([bundles, invoices, companies]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.items = uniqBy(invoices, 'groupId').map((invoice) => {
                        const emitter = this.bundleMap[invoice.emitterId];
                        const invoiceStatus = !invoice.emissionDate
                            ? invoice.conclusionDate
                                ? invoice.conclusion
                                : 'toValidate'
                            : 'toEmit';
                        return {
                            ...invoice,
                            index: invoice.versions.length,
                            versions: invoice.versions.map((version) => ({
                                ...version,
                                emitter: this.bundleMap[version.bundleId],
                            })),
                            emitter,
                            filterString: [
                                invoice.code,
                                invoice.object,
                                invoice.emitterId && emitter ? emitter.label : '',
                            ].join(),
                            invoiceStatusColor: this.invoiceStatusColors[invoiceStatus] || '',
                            invoiceStatus,
                        };
                    });
                    this.loading = false;
                }),
            ];
        },
        matchBundleFilter(item, bundleIds) {
            return item.emitter && bundleIds.includes(item.emitter.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) => item.invoiceStatus && criteria.id === 'invoice_' + item.invoiceStatus,
            );
        },
        filterFn(filter) {
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
        async restoreFilter() {
            const invoicesExport = await getInvoicesExport(
                this.$route.params.projectId,
                this.$route.params.invoicesExportId,
            );
            if (invoicesExport) {
                this.filterValue = invoicesExport.filter || [];
                this.sortKey = invoicesExport.sortKey || 'object';
            }
        },
    },
    data() {
        return {
            invoiceStatusColors: {
                toEmit: 'bg-amber-500',
                validated: 'bg-green-600',
                rejected: 'text-white bg-red-600',
            },
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: true,
            sortKey: 'object',
            sortAsc: true,
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
        };
    },
};
</script>
